import { useTranslation } from "@next-platform/client-common-core";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  EmptyState,
  Spinner,
  SpinnerSize,
  Text,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { useNavigate } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import { ChangeConversationTitleModal, ChatsMultiSelectButtonGroup, RemoveConversationModal } from "@/components/Chats";
import ChatCard from "@/components/Chats/ChatCard/ChatCard";
import ChatsListToolbar from "@/components/Chats/ChatsListToolbar";
import Search from "@/components/Chats/Search/Search";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import { Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useRecentChats from "@/hooks/useRecentChats";
import { IChatsConversation } from "@/types";

export default function Chats() {
  const { isMobile } = useMedia();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    conversations,
    filteredConversations,
    isConversationsLoading,
    isLoadingMoreConversations,
    isLoadMoreDisabled,
    isSortRefetching,
    isRemoveConversationLoading,
    lastCharacterId,
    loadMoreConversations,
    onUserRemoveConversation,
    selectedConversation,
    isChangeTitleModalVisible,
    setIsChangeTitleModalVisible,
    isRemoveConversationModalVisible,
    setIsRemoveConversationModalVisible,
    onUpdateConversationTitle,
    isChangeTitleLoading,
    getDropdownListItems,
    onSearch,
    onClearSearch,
    searchQuery,
    sortOrder,
    handleSortChange,
    multiSelectProps,
  } = useRecentChats();

  const {
    isMultiSelectMode,
    selectedConversationIds,
    toggleMultiSelectMode,
    toggleConversationSelection,
    selectAllConversations,
    cancelMultiSelect,
    openRemoveModalForMultiSelect,
  } = multiSelectProps;

  const hasNoData = !filteredConversations?.length;

  // Sort refetch uses isConversationsLoading too — keep toolbar/search visible; only disable Load More.
  if (isConversationsLoading && hasNoData && !isSortRefetching) {
    return <Spinner size={SpinnerSize.LARGE} className="m-auto" />;
  }

  return (
    <PageLayout size={PageSize.Medium} className="h-full flex flex-col">
      <PageStaticMetaTags pageId="chats" />
      <Row className="justify-between items-center">
        <Text variant={isMobile ? TextVariants.HEADING_6_BOLD : TextVariants.HEADING_4_BOLD} as="h1" className="my-0">
          <Trans i18nKey="chats:page.header.title" />
        </Text>
        <ChatsMultiSelectButtonGroup
          isMultiSelectMode={isMultiSelectMode}
          selectedCount={selectedConversationIds.size}
          totalCount={conversations?.length || 0}
          visibleCount={filteredConversations?.length || 0}
          onToggleMultiSelect={toggleMultiSelectMode}
          onSelectAll={selectAllConversations}
          onCancel={cancelMultiSelect}
          onRemoveSelected={openRemoveModalForMultiSelect}
        />
      </Row>

      {/* Search Input and Button */}
      <Search onSearch={onSearch} onClear={onClearSearch} />
      <ChatsListToolbar visibleCount={filteredConversations?.length || 0} sortOrder={sortOrder} onSortChange={handleSortChange} />

      <div className="flex-grow flex flex-col gap-md">
        {!!hasNoData && (
          <div className="m-auto">
            <EmptyState
              title={searchQuery ? t("chats:page.recent.noResultsFound.title") : t("chats:page.recent.emptyResult.title")}
              description={searchQuery ? t("chats:page.recent.noResultsFound.description") : t("chats:page.recent.emptyResult.description")}
              buttonText={t("chats:page.recent.button.exploreChatbots")}
              onClick={() => navigate("/")}
            />
          </div>
        )}
        {(filteredConversations || []).map(
          (conversation: IChatsConversation) =>
            conversation?.id && (
              <ChatCard
                key={conversation?.id}
                conversation={conversation}
                dropdownItems={getDropdownListItems(conversation)}
                isMultiSelectMode={isMultiSelectMode}
                isSelected={selectedConversationIds.has(conversation.id)}
                onToggleSelection={toggleConversationSelection}
              />
            ),
        )}
        {!!(lastCharacterId && !searchQuery) && (
          <Row justify="center" className="my-lg">
            <Button
              disabled={isLoadMoreDisabled}
              loading={isLoadingMoreConversations}
              onClick={() => void loadMoreConversations()}
              size={ButtonSize.LARGE}
              variant={ButtonVariant.OUTLINED}
            >
              <Trans i18nKey="common:loadMore" />
            </Button>
          </Row>
        )}
      </div>
      {!!(isChangeTitleModalVisible && selectedConversation) && (
        <ChangeConversationTitleModal
          conversationTitle={selectedConversation?.label}
          isOpen={isChangeTitleModalVisible}
          onOpenChange={setIsChangeTitleModalVisible}
          onSubmit={onUpdateConversationTitle}
          isLoading={isChangeTitleLoading}
        />
      )}

      {!!isRemoveConversationModalVisible && (
        <RemoveConversationModal
          isLoading={isRemoveConversationLoading}
          isOpen={isRemoveConversationModalVisible}
          onOpenChange={setIsRemoveConversationModalVisible}
          onSubmit={onUserRemoveConversation}
          isMultipleMessagesSelected={!!(isMultiSelectMode && selectedConversationIds.size > 1)}
        />
      )}
    </PageLayout>
  );
}
